import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useNavigate } from 'react-router-dom';
import { googleAnalyticsService, GA4Account, GA4Property } from '../../services/googleAnalytics';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { 
  Plug, 
  BarChart3, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle,
  Settings,
  Plus,
  ExternalLink,
  Loader,
  XCircle,
  ArrowLeftRight,
  X
} from 'lucide-react';

interface ProjectConnectionsProps {
  project: any;
}

interface ExtendedGA4Property extends GA4Property {
  accountId: string;
  accountName: string;
}

export function ProjectConnections({ project }: ProjectConnectionsProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState<Record<string, boolean>>({});
  const [connections, setConnections] = useState<any[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  
  // Property switching state
  const [showPropertySwitcher, setShowPropertySwitcher] = useState(false);
  const [availableProperties, setAvailableProperties] = useState<ExtendedGA4Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isSwitchingProperty, setIsSwitchingProperty] = useState(false);
  
  const navigate = useNavigate();

  // Load connections when component mounts
  useEffect(() => {
    loadConnections();
    checkConnectionStatus();
    
    // Refresh connections when page becomes visible (e.g., returning from OAuth flow)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadConnections();
        checkConnectionStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [project.id]);

  const checkConnectionStatus = async () => {
    setIsCheckingStatus(true);
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setConnectionStatus({ connected: false, reason: 'no_auth', needsReconnection: true });
        return;
      }

      // Check connection status using the new endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/analytics/projects/${project.id}/connection-status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        setConnectionStatus(status);
        console.log('Connection status:', status);
      } else {
        console.error('Failed to check connection status:', response.status);
        setConnectionStatus(null);
      }
    } catch (error: any) {
      console.error('Error checking connection status:', error);
      setConnectionStatus(null);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const loadConnections = async () => {
    setIsLoadingConnections(true);
    setConnectionError(null);
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setConnectionError('Authentication required. Please log in again.');
        return;
      }

      // Get project connections from the backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/oauth/google/project/${project.id}/connection`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 404) {
        // No connection found - this is normal for unconnected projects
        setConnections([]);
        return;
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          setConnectionError('Authentication required. Please log in again.');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.connection) {
        setConnections([data.connection]);
        console.log('Loaded connection data:', data.connection);
      } else {
        setConnections([]);
        if (data.error) {
          setConnectionError(data.error);
        }
      }
    } catch (error: any) {
      console.error('Error loading connections:', error);
      setConnections([]);
      setConnectionError(error.message || 'Failed to load connections');
    } finally {
      setIsLoadingConnections(false);
    }
  };

  const loadAvailableProperties = async () => {
    setIsLoadingProperties(true);
    try {
      const accounts = await googleAnalyticsService.getAccounts();
      
      // Flatten all properties from all accounts
      const allProperties = accounts.flatMap(account => 
        account.properties.map(property => ({
          ...property,
          accountId: account.accountId,
          accountName: account.accountName
        }))
      );
      
      setAvailableProperties(allProperties);
    } catch (error) {
      console.error('Error loading available properties:', error);
      toast.error('Failed to load available GA4 properties');
    } finally {
      setIsLoadingProperties(false);
    }
  };

  const handleShowPropertySwitcher = async () => {
    setShowPropertySwitcher(true);
    await loadAvailableProperties();
  };

  const handleSwitchProperty = async (propertyId: string, propertyName: string, accountId: string) => {
    setIsSwitchingProperty(true);
    try {
      // Use the dedicated switch endpoint that handles cleanup properly
      const result = await googleAnalyticsService.switchProperty(
        accountId,
        propertyId,
        propertyName,
        project.id
      );

      if (result.success) {
        toast.success(`Successfully switched to ${propertyName}!`);
        toast.info('🔧 Setting up metadata for the new property - this may take a few moments');
        
        // Close the switcher and reload connections
        setShowPropertySwitcher(false);
        await loadConnections();
      } else {
        throw new Error(result.error || 'Failed to switch property');
      }
    } catch (error) {
      console.error('Error switching property:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to switch property');
    } finally {
      setIsSwitchingProperty(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // Store the project ID in localStorage so we can retrieve it after the OAuth flow
      localStorage.setItem('connecting_project_id', project.id);
      
      const result = await googleAnalyticsService.initiateOAuth();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to start connection process');
      }
      
      // OAuth redirect will happen automatically
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect to Google Analytics');
      setIsConnecting(false);
    }
  };

  const handleReconnect = async () => {
    setIsConnecting(true);
    try {
      // Store the project ID and mark as reconnection
      localStorage.setItem('connecting_project_id', project.id);
      localStorage.setItem('reconnecting_ga4', 'true');
      
      toast.info('Starting GA4 reconnection process...');
      
      const result = await googleAnalyticsService.initiateOAuth();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to start reconnection process');
      }
      
      // OAuth redirect will happen automatically
    } catch (error) {
      console.error('Reconnection error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reconnect to Google Analytics');
      setIsConnecting(false);
    }
  };

  const handleSync = async (connectionId: string) => {
    setIsSyncing(prev => ({ ...prev, [connectionId]: true }));
    
    try {
      // Simulate sync process
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Data synchronized successfully');
      
      // Refresh connections
      await loadConnections();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to synchronize data');
    } finally {
      setIsSyncing(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  const handleRefresh = async () => {
    await loadConnections();
    toast.success('Connections refreshed');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'destructive';
      case 'pending': return 'secondary';
      case 'disconnected': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'error': return <AlertCircle className="w-3 h-3 mr-1" />;
      case 'pending': return <RefreshCw className="w-3 h-3 mr-1 animate-spin" />;
      case 'disconnected': return <XCircle className="w-3 h-3 mr-1" />;
      default: return null;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
            <Plug className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Data Connections</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Connect your data sources to enable AI insights</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoadingConnections}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingConnections ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={handleConnect}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            disabled={isConnecting}
          >
            {isConnecting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add Connection
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Connection Status Banner */}
      {connectionStatus && connectionStatus.needsReconnection && (
        <Card className="p-6 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mr-3">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-red-800 dark:text-red-300">GA4 Connection Needs Attention</h3>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {connectionStatus.reason === 'expired_tokens' 
                    ? 'Your Google Analytics tokens have expired and need to be refreshed'
                    : connectionStatus.reason === 'no_tokens'
                    ? 'OAuth tokens are missing from your GA4 connection'
                    : connectionStatus.reason === 'no_connection'
                    ? 'No GA4 connection found for this project'
                    : connectionStatus.message || 'Connection requires attention'
                  }
                </p>
              </div>
            </div>
            <Button 
              onClick={handleReconnect}
              disabled={isConnecting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isConnecting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Reconnecting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reconnect GA4
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Error State */}
      {connectionError && !isLoadingConnections && (
        <Card className="p-6 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
            <div>
              <p className="text-red-800 dark:text-red-300 font-medium">Failed to load connections</p>
              <p className="text-red-600 dark:text-red-400 text-sm">{connectionError}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoadingConnections && (
        <Card className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin mr-3" />
            <p className="text-gray-600 dark:text-gray-300">Loading connections...</p>
          </div>
        </Card>
      )}

      {/* Connections List */}
      {!isLoadingConnections && connections.length > 0 ? (
        <div className="space-y-4">
          {connections.map((connection: any, index: number) => (
            <Card key={connection.connection?.id || index} className="p-6 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Google Analytics 4
                      </h3>
                      <Badge variant={getStatusColor(connection.connection?.status || 'connected')}>
                        {getStatusIcon(connection.connection?.status || 'connected')}
                        {(connection.connection?.status || 'connected').charAt(0).toUpperCase() + (connection.connection?.status || 'connected').slice(1)}
                      </Badge>
                      <Badge variant="success">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Property Name</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {connection.summary?.property_name || connection.connection?.connection_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Property ID</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {connection.summary?.property_id || connection.connection?.property_id || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Account ID</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {connection.summary?.account_id || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Connected At</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatDate(connection.summary?.connected_at || connection.connection?.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Last Synced</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatDate(connection.connection?.last_sync_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">API Key Status</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {connection.api_key?.is_active ? (
                            <span className="text-green-600 dark:text-green-400">Active</span>
                          ) : (
                            <span className="text-red-600 dark:text-red-400">Inactive</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Connection Health Status */}
                    <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {connection.summary?.is_fully_connected ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                              <span className="text-sm font-medium text-green-800 dark:text-green-300">Fully Connected</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Partial Connection</span>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {connection.connection?.id}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleShowPropertySwitcher}
                    className="text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    Change Property
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSync(connection.connection?.id)}
                    disabled={isSyncing[connection.connection?.id]}
                  >
                    {isSyncing[connection.connection?.id] ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync Now
                      </>
                    )}
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : !isLoadingConnections && (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plug className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No connections yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
            Connect your data sources to unlock powerful AI-driven insights and analytics.
          </p>
          <div className="space-y-4">
            <Button 
              onClick={handleConnect}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Connect Google Analytics 4
                </>
              )}
            </Button>
            <div className="flex justify-center">
              <a 
                href="https://support.google.com/analytics/answer/9304153" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                How to find your GA4 property ID
              </a>
            </div>
          </div>
        </Card>
      )}

      {/* Property Switcher Modal */}
      {showPropertySwitcher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Change GA4 Property</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Select a different Google Analytics 4 property for this project</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPropertySwitcher(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingProperties ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin mr-3" />
                  <p className="text-gray-600 dark:text-gray-300">Loading available properties...</p>
                </div>
              ) : availableProperties.length > 0 ? (
                <div className="space-y-3">
                  {availableProperties.map((property: ExtendedGA4Property) => {
                    const isCurrentProperty = property.propertyId === connections[0]?.summary?.property_id;
                    
                    return (
                      <div 
                        key={property.propertyId}
                        className={`border rounded-lg p-4 transition-all ${
                          isCurrentProperty 
                            ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20' 
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!isCurrentProperty && !isSwitchingProperty) {
                            handleSwitchProperty(property.propertyId, property.propertyName, property.accountId);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">{property.propertyName}</h4>
                              {isCurrentProperty && (
                                <Badge variant="success">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Current
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                              <div>
                                <span className="font-medium">Property ID:</span> {property.propertyId}
                              </div>
                              <div>
                                <span className="font-medium">Account:</span> {property.accountName}
                              </div>
                              {property.websiteUrl && (
                                <div className="col-span-2">
                                  <span className="font-medium">Website:</span> {property.websiteUrl}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {!isCurrentProperty && (
                            <Button 
                              size="sm"
                              disabled={isSwitchingProperty}
                              className="ml-4"
                            >
                              {isSwitchingProperty ? (
                                <>
                                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                                  Switching...
                                </>
                              ) : (
                                'Select'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-300 mb-4">No other GA4 properties found in your account.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPropertySwitcher(false)}
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Connections */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Connections</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { 
              name: 'Google Analytics 4', 
              icon: BarChart3, 
              connected: connections.length > 0,
              description: 'Website and app analytics'
            },
            { 
              name: 'Google Search Console', 
              icon: BarChart3, 
              connected: false,
              description: 'Search performance data'
            },
            { 
              name: 'Google Ads', 
              icon: BarChart3, 
              connected: false,
              description: 'Advertising campaign data'
            },
            { 
              name: 'Facebook Ads', 
              icon: BarChart3, 
              connected: false,
              description: 'Social media advertising'
            },
            { 
              name: 'Shopify', 
              icon: BarChart3, 
              connected: false,
              description: 'E-commerce analytics'
            },
            { 
              name: 'HubSpot', 
              icon: BarChart3, 
              connected: false,
              description: 'CRM and marketing data'
            }
          ].map((connection, index) => (
            <div 
              key={index}
              className={`border rounded-lg p-4 transition-all ${
                connection.connected 
                  ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                    connection.connected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <connection.icon className={`w-4 h-4 ${
                      connection.connected ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{connection.name}</span>
                  </div>
                </div>
                {connection.connected ? (
                  <Badge variant="success">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleConnect}
                    disabled={isConnecting || connection.name !== 'Google Analytics 4'}
                  >
                    {connection.name === 'Google Analytics 4' ? (
                      isConnecting ? 'Connecting...' : 'Connect'
                    ) : (
                      'Coming Soon'
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{connection.description}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}